import { useEffect } from 'react';
import { useSyncSystem } from '@/lib/sync/sync-system-context';

/**
 * Publish the current sub-view (`viewId`) into the entity's awareness local
 * state, so view-scoped presence (`useAwareness(entityId, viewId)`) can show
 * only the peers co-present on the same view.
 *
 * For a multi-surface entity (e.g. a set of derived tabs), the host view
 * calls this with its active tab key. A single-surface entity never calls
 * it — it simply never publishes a `viewId`, and presence is unscoped
 * (legacy behavior).
 *
 * Uses `setLocalState({ ...existing, viewId })` — NOT `setLocalStateField`,
 * which is a silent no-op when local state is null (a known footgun). On unmount
 * (leaving the entity) it strips `viewId` so the user no longer reads as present
 * on that view, preserving every other awareness field (`user`, cursors, …).
 *
 * `enabled` (default true) lets a host skip publishing without breaking the
 * rules-of-hooks — e.g. an embedded editor whose host already owns a publish
 * for the same entity must not publish a second time onto the same awareness.
 */
export function usePublishAwarenessView(
  entityId: string,
  viewId: string,
  enabled = true,
): void {
  const { registry } = useSyncSystem();

  useEffect(() => {
    if (!enabled) return;
    const awareness = registry.getAwareness(entityId);
    if (!awareness) return;

    const existing = awareness.getLocalState() ?? {};
    awareness.setLocalState({ ...existing, viewId });

    return () => {
      const aw = registry.getAwareness(entityId);
      if (!aw) return;
      const current = aw.getLocalState();
      // A sibling's cleanup (e.g. useEntityYjs's stopViewing) may have already
      // nulled local state — `?? {}` would resurrect it as a non-null, userless
      // object. Only strip viewId when there is still state to strip it from.
      if (!current) return;
      // Strip viewId, preserve everything else.
      const { viewId: _drop, ...rest } = current as Record<string, unknown>;
      aw.setLocalState(rest);
    };
  }, [registry, entityId, viewId, enabled]);
}
